// animations/loop-animations.ts
import gsap from 'gsap';

import { cleanup } from './dot-cms-aniamtion.helper';
import { baseBounce, baseFade } from './common-base-animations';
import { AnimationDynamicSettings, AnimationType } from './animation/animations.model';

export const loopAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts: gsap.TweenVars) => gsap.core.Tween>
> = {
  // Use common base animations with loop options
  fade: (_s, el, opts) => baseFade(el, { repeat: -1, yoyo: true, ...opts }),
  flip: (s, el, opts) => {
    cleanup(el);
    if ((s.orientation ?? 'horizontal') === 'horizontal') {
      // Horizontal flip uses rotationY (flips left-right)
      return gsap.to(el, { rotationY: 180, repeat: -1, yoyo: true, ...opts });
    }

    // Vertical flip uses rotationX (flips up-down)
    return gsap.to(el, { rotationX: 180, repeat: -1, yoyo: true, ...opts });
  },
  flap: (s, el, opts) => {
    cleanup(el);
    const scaleAmt = 1 - (s.intensity ?? 0.3);
    return gsap.to(el, {
      scaleX: s.direction === 'left' || s.direction === 'right' ? scaleAmt : 1,
      scaleY: s.direction === 'top' || s.direction === 'bottom' ? scaleAmt : 1,
      repeat: -1,
      yoyo: true,
      ...opts,
    });
  },
  bounce: (_s, el, opts) => baseBounce(el, { repeat: -1, yoyo: true, ease: 'bounce.inOut', ...opts }),

  // Loop-specific animations
  breathe: (s, el, opts) => {
    cleanup(el);
    const scaleAmount = s.breathe_scale ?? 1.2;
    if (s.orientation === 'horizontal') {
      return gsap.to(el, { scaleX: scaleAmount, repeat: -1, yoyo: true, ...opts });
    }
    return gsap.to(el, { scaleY: scaleAmount, repeat: -1, yoyo: true, ...opts });
  },

  pulse: (s, el, opts) => {
    cleanup(el);
    const scaleAmt = s.pulse_scale ?? 1.5;
    return gsap.to(el, { scale: scaleAmt, repeat: -1, yoyo: true, ...opts });
  },

  spin: (s, el, opts) => {
    cleanup(el);
    const spinCount = s.spin_count ?? 1;
    const scale = s.scale ?? 1;
    const direction = s.direction ?? 'clockwise';
    const rotDir = direction === 'counter-clockwise' ? -360 : 360;

    return gsap.fromTo(
      el,
      { rotation: 0, scale: 1 },
      {
        rotation: rotDir * spinCount,
        scale,
        ease: opts.ease || 'linear',
        yoyo: true,
        repeat: typeof opts.repeat === 'number' ? opts.repeat : -1,
        ...opts,
      }
    );
  },

  poke: (s, el, opts) => {
    cleanup(el);
    const scaleAmt = 1 + (s.intensity ?? 0.25);
    return gsap.to(el, { scale: scaleAmt, repeat: -1, yoyo: true, ...opts });
  },

  flash: (s, el, opts) => gsap.to(el, { opacity: s.intensity ?? 0.8, repeat: -1, yoyo: true, ...opts }),

  swing: (s, el, opts) => gsap.to(el, { rotation: s.angle ?? 15, repeat: -1, yoyo: true, ease: opts.ease, ...opts }),

  rubber: (s, el, opts) => {
    cleanup(el);
    const intensity = s.intensity ?? 0.4;
    const scaleX = 1 + intensity / 2;
    return gsap.to(el, { scaleX, scaleY: 1 / scaleX, repeat: -1, yoyo: true, ease: 'elastic.inOut', ...opts });
  },

  cross: (s, el, opts) =>
    gsap.to(el, {
      rotationX: s.cross ?? 25,
      rotationY: s.cross ?? 25,
      repeat: -1,
      yoyo: true,
      ...opts,
    }),

  jello: (s, el, opts) =>
    gsap.to(el, {
      skewX: (s.jello ?? 0.35) * 50,
      skewY: (s.jello ?? 0.35) * 25,
      repeat: -1,
      yoyo: true,
      ease: 'elastic.inOut',
      ...opts,
    }),

  wiggle: (s, el, opts) =>
    gsap.to(el, {
      x: (s.wiggle ?? 0.2) * 20,
      rotation: (s.wiggle ?? 0.2) * 10,
      repeat: -1,
      yoyo: true,
      ...opts,
    }),
};
